import { useCallback, useEffect, useRef, useState } from "react";
import type { AlbumManifest } from "./manifest";
import { buildStubManifest } from "./stubManifest";
import { LIBRARY_VERSION } from "./layouts";

/** Warn (dev) if the manifest's layout library differs from ours — geometry
 *  could drift; the fix is re-running sync:layouts against the matching engine. */
function pinLayoutVersion(m: AlbumManifest) {
  if (
    import.meta.env?.DEV &&
    m.layoutLibraryVersion &&
    m.layoutLibraryVersion !== LIBRARY_VERSION
  ) {
    // eslint-disable-next-line no-console
    console.warn(
      `[layouts] manifest layoutLibraryVersion ${m.layoutLibraryVersion} != bundled ${LIBRARY_VERSION}`,
    );
  }
}

// API base for the album endpoints. Set via Vite env (VITE_API_BASE) at build;
// absent in local dev => stub.
const API_BASE = import.meta.env?.VITE_API_BASE as string | undefined;
const params = new URLSearchParams(window.location.search);

// Gate contract (Anvil frozen):
//   GET  /a/:token        -> 200 { gated:true, sender?:{name}, version, cover? }
//                           cover = PUBLIC book-jacket image URL (photo+title);
//                           no other interior. OR 200 <full manifest> (unlocked)
//                           410 revoked · 404 not found
//   POST /a/:token/unlock { code } -> 200 <full manifest> · 401 wrong · 429 throttled
export interface GateInfo {
  sender: string | null;
  /** Public cover image URL (square book-jacket) shown as the gate hero, or
   *  null on an older backend / before the cover renders — falls back to the
   *  lock-disc layout. */
  cover: string | null;
}
export type ManifestState =
  | { status: "loading" }
  | { status: "gated"; gate: GateInfo }
  | { status: "ready"; manifest: AlbumManifest }
  | { status: "revoked" } // 410 Gone — sharing turned off
  | { status: "notfound" } // 404 — unknown token
  | { status: "error"; message: string };

/** Outcome of a code submission (drives the gate page's error states). */
export type UnlockResult = "ok" | "wrong" | "rate_limited" | "error";

/** Parse the shareToken from /a/:token. */
export function shareTokenFromPath(pathname = window.location.pathname): string | null {
  const m = pathname.match(/^\/a\/([^/]+)/);
  return m ? m[1] : null;
}

function ready(manifest: AlbumManifest): ManifestState {
  pinLayoutVersion(manifest);
  return { status: "ready", manifest };
}

// Session persistence (Iris/Anvil): cache the unlocked manifest + code for the
// browser session so a refresh doesn't re-prompt; a new session/device re-gates.
// The code is also re-sent on checkout (server is stateless, code-per-request).
const cacheKey = (token: string) => `minnea_gate_${token}`;

function readGateCache(token: string): { code: string; manifest: AlbumManifest } | null {
  try {
    const raw = sessionStorage.getItem(cacheKey(token));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
function writeGateCache(token: string, code: string, manifest: AlbumManifest) {
  try {
    sessionStorage.setItem(cacheKey(token), JSON.stringify({ code, manifest }));
  } catch {
    /* sessionStorage unavailable (private mode / quota) — degrade to re-prompt */
  }
}

/** The unlocked code for the current album, for re-sending on checkout. */
export function getUnlockCode(): string | null {
  const token = shareTokenFromPath();
  return token ? (readGateCache(token)?.code ?? null) : null;
}
function clearGateCache(token: string) {
  try {
    sessionStorage.removeItem(cacheKey(token));
  } catch {
    /* ignore */
  }
}

/** Outcome of an unlock POST — shared by the gate submit AND the freshness
 *  refetch (mount + tab-focus), so returning viewers always pull the latest
 *  approved photos rather than a cached snapshot. */
type UnlockOutcome =
  | { kind: "ok"; manifest: AlbumManifest }
  | { kind: "wrong" } // 401 — code no longer valid (rotated / unshared)
  | { kind: "revoked" } // 404/410 — album gone → dead-link card
  | { kind: "rate_limited" } // 429
  | { kind: "error" }; // network / other — keep any cached copy

async function postUnlock(token: string, code: string): Promise<UnlockOutcome> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/a/${encodeURIComponent(token)}/unlock`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ code }),
    });
  } catch {
    return { kind: "error" };
  }
  if (res.status === 401) return { kind: "wrong" };
  if (res.status === 429) return { kind: "rate_limited" };
  if (res.status === 404 || res.status === 410) return { kind: "revoked" };
  if (!res.ok) return { kind: "error" };
  try {
    return { kind: "ok", manifest: (await res.json()) as AlbumManifest };
  } catch {
    return { kind: "error" };
  }
}

// How often to ask the gate whether the album changed (cheap GET, never the
// rate-limited /unlock). Only while the tab is open + visible.
const POLL_MS = 60_000;

export function useAlbum(): {
  state: ManifestState;
  unlock: (code: string) => Promise<UnlockResult>;
  hasUpdate: boolean;
  applyUpdate: () => void;
  dismissUpdate: () => void;
} {
  const [state, setState] = useState<ManifestState>({ status: "loading" });
  const [hasUpdate, setHasUpdate] = useState(false);
  const tokenRef = useRef<string | null>(shareTokenFromPath());
  // Version of the manifest currently on screen — the poll compares the gate's
  // reported version against this to decide whether new photos have landed.
  const versionRef = useRef<string | undefined>(undefined);
  // The newest version the poll has surfaced, and the one the viewer dismissed —
  // so a dismissed pill re-appears only on the NEXT distinct change, not the same
  // one (no nag).
  const pendingVersionRef = useRef<string | undefined>(undefined);
  const dismissedVersionRef = useRef<string | undefined>(undefined);

  // Render a manifest: track its version + clear any pending "new photos" flag
  // (whatever we just rendered IS the latest). All ready-renders go through here.
  const showManifest = useCallback((m: AlbumManifest) => {
    versionRef.current = m.version;
    setHasUpdate(false);
    setState(ready(m));
  }, []);

  // Initial load. With a remembered code we ALWAYS refetch fresh (re-POST
  // unlock) so a returning viewer sees photos added since — the cached manifest
  // is only an offline fallback. Without a code, GET decides gate vs dead-link.
  useEffect(() => {
    const token = tokenRef.current;

    // Dev / no backend: stub. ?gate previews the gate page; a remembered unlock
    // opens the reader; otherwise open straight in.
    if (!API_BASE || !token) {
      const stub = buildStubManifest();
      const cached = token ? readGateCache(token) : null;
      if (cached) showManifest(cached.manifest);
      else if (params.has("gate")) setState({ status: "gated", gate: { sender: stub.sender.name, cover: null } });
      else showManifest(stub);
      return;
    }

    let cancelled = false;
    (async () => {
      const cached = readGateCache(token);
      if (cached?.code) {
        // Unlocked this session — refetch fresh with the remembered code (no
        // re-prompt), so the latest approved photos always render.
        const out = await postUnlock(token, cached.code);
        if (cancelled) return;
        if (out.kind === "ok") {
          writeGateCache(token, cached.code, out.manifest);
          return showManifest(out.manifest);
        }
        if (out.kind === "revoked") {
          clearGateCache(token);
          return setState({ status: "revoked" });
        }
        if (out.kind === "error" || out.kind === "rate_limited") {
          // Offline / throttled — show the last-known album, don't block.
          return showManifest(cached.manifest);
        }
        // "wrong": code rotated / album unshared → drop it and fall through to
        // GET, which re-gates (or shows the dead-link card).
        clearGateCache(token);
      }

      // No usable cached code — GET decides gate vs dead-link.
      try {
        const res = await fetch(`${API_BASE}/a/${encodeURIComponent(token)}`);
        if (cancelled) return;
        if (res.status === 410) return setState({ status: "revoked" });
        if (res.status === 404) return setState({ status: "notfound" });
        if (!res.ok) return setState({ status: "error", message: `HTTP ${res.status}` });
        const data = await res.json();
        if (data && data.gated === true) {
          setState({ status: "gated", gate: { sender: data.sender?.name ?? null, cover: data.cover ?? null } });
        } else {
          showManifest(data as AlbumManifest);
        }
      } catch (err) {
        if (!cancelled) {
          setState({ status: "error", message: (err as Error).message });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showManifest]);

  // Freshness on return: when the viewer refocuses the tab, silently pull the
  // latest photos with the remembered code. Only disrupts the reader on a hard
  // signal (album gone → dead card, code rotated → re-gate); transient errors
  // keep whatever is already on screen.
  useEffect(() => {
    const token = tokenRef.current;
    if (!API_BASE || !token) return;
    let inFlight = false;
    let lastRefresh = 0;
    const MIN_GAP_MS = 15_000; // don't hammer /unlock on rapid tab-switching
    const refresh = async () => {
      if (inFlight || document.visibilityState !== "visible") return;
      if (Date.now() - lastRefresh < MIN_GAP_MS) return;
      const cached = readGateCache(token);
      if (!cached?.code) return; // not unlocked → nothing to refresh
      inFlight = true;
      lastRefresh = Date.now();
      const out = await postUnlock(token, cached.code);
      inFlight = false;
      if (out.kind === "ok") {
        writeGateCache(token, cached.code, out.manifest);
        showManifest(out.manifest);
      } else if (out.kind === "revoked") {
        clearGateCache(token);
        setState({ status: "revoked" });
      } else if (out.kind === "wrong") {
        clearGateCache(token);
        setState({ status: "gated", gate: { sender: null, cover: null } });
      }
      // error / rate_limited → keep the current view (no disruption).
    };
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("focus", refresh);
    return () => {
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, [showManifest]);

  // Live "new photos" poll: while a manifest is on screen and the tab is
  // visible, cheaply ask the GATE (GET, never the rate-limited /unlock) whether
  // the album's version moved. If it did, raise the flag — the reader shows a
  // subtle refresh affordance; the actual refetch happens on apply. Inert until
  // the gate returns a `version` (older backend → no field → never raises).
  useEffect(() => {
    const token = tokenRef.current;
    if (!API_BASE || !token) return;
    let stopped = false;
    const check = async () => {
      if (document.visibilityState !== "visible") return;
      if (!versionRef.current) return; // nothing rendered yet
      if (!readGateCache(token)?.code) return; // only while unlocked
      try {
        const res = await fetch(`${API_BASE}/a/${encodeURIComponent(token)}`);
        if (stopped || !res.ok) return;
        const data = await res.json();
        const v = data?.version;
        if (v && v !== versionRef.current && v !== dismissedVersionRef.current) {
          pendingVersionRef.current = v;
          setHasUpdate(true);
        }
      } catch {
        /* transient — retry next tick */
      }
    };
    const id = window.setInterval(check, POLL_MS);
    return () => {
      stopped = true;
      window.clearInterval(id);
    };
  }, []);

  const unlock = useCallback(
    async (code: string): Promise<UnlockResult> => {
      const token = tokenRef.current;
      const trimmed = code.trim();

      // Dev path: validate against the stub's joinCode.
      if (!API_BASE || !token) {
        const stub = buildStubManifest();
        if (trimmed.toUpperCase() === stub.joinCode.toUpperCase()) {
          if (token) writeGateCache(token, trimmed, stub);
          showManifest(stub);
          return "ok";
        }
        return "wrong";
      }

      const out = await postUnlock(token, trimmed);
      if (out.kind === "ok") {
        writeGateCache(token, trimmed, out.manifest);
        showManifest(out.manifest);
        return "ok";
      }
      if (out.kind === "wrong") return "wrong";
      if (out.kind === "rate_limited") return "rate_limited";
      if (out.kind === "revoked") {
        setState({ status: "revoked" }); // parent swaps to the dead-link card
        return "error";
      }
      return "error";
    },
    [showManifest],
  );

  // Pull the latest now (viewer tapped the "new photos" affordance). Re-fetch
  // via the freshness path and re-render; drop the flag on any resolution.
  const applyUpdate = useCallback(() => {
    const token = tokenRef.current;
    const code = token ? readGateCache(token)?.code : null;
    if (!token || !code) {
      setHasUpdate(false);
      return;
    }
    void (async () => {
      const out = await postUnlock(token, code);
      if (out.kind === "ok") {
        writeGateCache(token, code, out.manifest);
        showManifest(out.manifest);
      } else if (out.kind === "revoked") {
        clearGateCache(token);
        setState({ status: "revoked" });
      } else if (out.kind === "wrong") {
        clearGateCache(token);
        setState({ status: "gated", gate: { sender: null, cover: null } });
      } else {
        setHasUpdate(false); // transient — drop the pill; the poll re-raises it
      }
    })();
  }, [showManifest]);

  // Dismiss the affordance without refreshing — hide it and remember which
  // version was dismissed so the poll only re-raises on a genuinely newer change.
  const dismissUpdate = useCallback(() => {
    dismissedVersionRef.current = pendingVersionRef.current;
    setHasUpdate(false);
  }, []);

  return { state, unlock, hasUpdate, applyUpdate, dismissUpdate };
}
